import { importItemComponents, groupItemComponents } from '../composables/itemComponents'
import { useThemeStore } from './themeStore'
import { useSceneStore } from './sceneStore'
import { useDisappearableStore } from './disappearableStore'

export const useSceneItemsStore = defineStore('sceneItems', () => {
  const sceneStore = useSceneStore()
  const themeStore = useThemeStore()
  const disappearableStore = useDisappearableStore()

  const currentItemComponents = shallowRef([])
  const currentItemComponentsUngrouped = shallowRef([])
  let lastUpdatedItemsForSceneId = null

  let abortController = new AbortController()

  const setItems = async (items) => {
    abortController.abort('Received new items, aborting previous transition to new items')
    const localAbortController = new AbortController()
    abortController = localAbortController

    const reorderedItems = items.reverse()

    const newItemComponents = await importItemComponents(themeStore.theme, reorderedItems)

    localAbortController.signal.throwIfAborted()

    const newItemComponentsWithKeys = assignKeys(currentItemComponentsUngrouped.value, newItemComponents)

    const groupedNewItemComponents = await groupItemComponents(themeStore.theme, newItemComponentsWithKeys)

    localAbortController.signal.throwIfAborted()

    if (lastUpdatedItemsForSceneId === sceneStore.scene?.id || !sceneStore.scene.forceSceneStart) {
      const itemComponentsToDisappear = setDifference(currentItemComponentsUngrouped.value, newItemComponentsWithKeys, (itemComponent) => itemComponent.key)
      const groupItemComponentsToDisappear = setDifference(currentItemComponents.value, groupedNewItemComponents, (itemComponent) => itemComponent.key).filter(({ groupId }) => groupId)

      const allToDisappear = [...itemComponentsToDisappear, ...groupItemComponentsToDisappear]

      // Immediately consider disappearing items as gone, since we may abort after disappearing but before unmounting/switching components.
      // When you go quickly from scene A to B to A: you start disappearing items from A to prepare for B, but then abort that to stay on A, but then the items have already disappeared but are still mounted.
      // To prevent that issue, we make sure to remove them from the (non-rendered) current item components list, which causes the disappeared items to get assigned new keys when they should reappear.
      currentItemComponentsUngrouped.value = currentItemComponentsUngrouped.value.filter((itemComponent) => !allToDisappear.find((toDisappear) => toDisappear.key === itemComponent.key))

      await disappearableStore.allDisappeared(allToDisappear)
    }

    localAbortController.signal.throwIfAborted()

    currentItemComponents.value = groupedNewItemComponents
    currentItemComponentsUngrouped.value = newItemComponentsWithKeys
    lastUpdatedItemsForSceneId = sceneStore.scene?.id
  }

  return {
    currentItemComponents,
    currentItemComponentsUngrouped,
    setItems,
  }
})

const assignKeys = (currentItemComponents, newItemComponents) => {
  const itemComponentsDisappeared = setDifference(currentItemComponents, newItemComponents, (itemComponent) => itemComponent.item?.id)

  return newItemComponents.map((itemComponent) => {
    const previousItemComponentToReuse =
      itemComponent.reuseComponent &&
      (currentItemComponents.find((currentItemComponent) => currentItemComponent.item?.id === itemComponent.item?.id) ||
        itemComponentsDisappeared.find((itemComponentDisappeared) => itemComponentDisappeared.item?.type === itemComponent.item?.type))

    const key =
      previousItemComponentToReuse?.key ?? // Reuse key if we are reusing a previous component _type_
      currentItemComponents.find((current) => current.item?.id === itemComponent.item?.id)?.key ?? // Pick previously generated key if the same item is still present
      `${itemComponent.key}${Math.random().toString(36).substring(2, 15)}` // Generate new key

    return {
      ...itemComponent,
      key,
      reusingComponent: !!previousItemComponentToReuse,
    }
  })
}

// Find everything that is in set A but not in set B.
const setDifference = (setA, setB, identifierCallback) => setA.filter((itemA) => !setB.find((itemB) => identifierCallback(itemA) === identifierCallback(itemB)))
